import React from "react";

import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { ArrowUpRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { makeTooltip } from "@/components/ui/tooltip";
import {
  AREA_AUDIT_SEVERITY_LABEL,
  AREA_AUDIT_STATUS_LABEL,
  areaAuditAreaHref,
  areaAuditDismissNote,
  areaAuditEvidenceSummary,
  areaAuditKindHint,
  areaAuditKindLabel,
  areaAuditRowStatus,
  areaAuditVenueHref,
} from "@/lib/tina/area-audit";
import { cn } from "@/lib/utils";
import type { AreaAuditFindingRow, AreaAuditSeverity } from "@/types/tina";

export interface UseAreaAuditColumnsOptions {
  /** Opens the dismiss dialog for one finding. */
  onDismiss: (row: AreaAuditFindingRow) => void;
  /** The finding currently being dismissed, so its button can go quiet. */
  dismissingId: string | null;
}

const SEVERITY_TOOLTIP: Record<AreaAuditSeverity, string> = {
  high: "A whole city is held back from getting parts",
  medium: "A venue or an area is left with no place in the city's layout",
  low: "Worth a look; nothing is missing from the layout",
};

const severityBadgeClass = (severity: string): string | undefined =>
  severity === "medium"
    ? "border-amber-500/50 text-amber-700 dark:text-amber-400"
    : undefined;

const fmt = (iso: string | null | undefined, pattern: string): string => {
  if (!iso) return "—";
  const at = dayjs(iso);
  return at.isValid() ? at.format(pattern) : "—";
};

const dash = <span className="text-xs text-muted-foreground">—</span>;

/** A link out of a finding, or a dash when the finding is not keyed to one. */
const LinkCell = ({
  href,
  label,
  id,
}: {
  href: string | null;
  label: string | null;
  id: string | null;
}) => {
  if (!href || !id) return dash;
  return (
    <a
      href={href}
      className="group inline-flex items-center gap-1 text-xs font-medium hover:underline"
      title={id}
      onClick={(event) => event.stopPropagation()}
    >
      <span className="truncate">{label || id}</span>
      <ArrowUpRight className="h-3 w-3 shrink-0 text-muted-foreground" />
    </a>
  );
};

/**
 * Columns for the Area tab of `/tina/audits`.
 *
 * **Nothing here carries a column filter or a sort.** `GET /area-audit/findings` filters
 * on exactly four params — `status`, `kind`, `severity`, `city` — all owned by the tab's
 * own toolbar as `permanent` filters, and its order is fixed like the venue route's. A
 * header that looked sortable would silently do nothing.
 *
 * The area links to its `/areas` edit page and the venue to the page the Venue tab links:
 * fixing a finding means editing one of those, never anything on this page.
 */
export const useAreaAuditColumns = ({
  onDismiss,
  dismissingId,
}: UseAreaAuditColumnsOptions) =>
  React.useMemo<ColumnDef<AreaAuditFindingRow>[]>(
    () => [
      {
        id: "kind",
        accessorKey: "kind",
        header: "What",
        size: 180,
        cell: ({ row }) =>
          makeTooltip(
            <Badge variant="outline" className="text-[10px] font-normal">
              {areaAuditKindLabel(row.original.kind)}
            </Badge>,
            // A kind this build does not know still shows its raw name.
            areaAuditKindHint(row.original.kind) ?? row.original.kind,
          ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "severity",
        accessorKey: "severity",
        header: "Severity",
        size: 90,
        cell: ({ row }) => {
          const severity = row.original.severity;
          return makeTooltip(
            <Badge
              variant={
                severity === "high"
                  ? "destructive"
                  : severity === "medium"
                    ? "outline"
                    : "secondary"
              }
              className={cn(
                "text-[10px] uppercase",
                severityBadgeClass(severity),
              )}
            >
              {severity in AREA_AUDIT_SEVERITY_LABEL
                ? AREA_AUDIT_SEVERITY_LABEL[severity]
                : String(severity)}
            </Badge>,
            SEVERITY_TOOLTIP[severity] ?? null,
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "status",
        header: "Status",
        size: 100,
        cell: ({ row }) => {
          const finding = row.original;
          const status = areaAuditRowStatus(finding);
          const label = (
            <span
              className={cn(
                "text-xs",
                status === "open" ? "font-medium" : "text-muted-foreground",
              )}
            >
              {AREA_AUDIT_STATUS_LABEL[status]}
            </span>
          );
          if (status === "dismissed") {
            const note = areaAuditDismissNote(finding);
            return makeTooltip(
              label,
              `${fmt(finding.dismissed_at, "DD/MM/YYYY HH:mm")}${
                finding.dismissed_by ? ` by ${finding.dismissed_by}` : ""
              }${note ? ` — ${note}` : ""}`,
            );
          }
          if (status === "resolved") {
            return makeTooltip(
              label,
              `The audit stopped seeing this on ${fmt(finding.resolved_at, "DD/MM/YYYY HH:mm")}`,
            );
          }
          return label;
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "city",
        header: "City",
        size: 130,
        cell: ({ row }) => (
          <span className="text-xs">
            {row.original.city || "—"}
            {row.original.country_code ? (
              <span className="ml-1 text-muted-foreground">
                {row.original.country_code}
              </span>
            ) : null}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "area",
        header: "Area",
        size: 170,
        cell: ({ row }) => (
          <LinkCell
            href={
              row.original.area_id
                ? areaAuditAreaHref(row.original.area_id)
                : null
            }
            label={row.original.area_name}
            id={row.original.area_id}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "venue",
        header: "Venue",
        size: 190,
        cell: ({ row }) => (
          <LinkCell
            href={
              row.original.venue_id
                ? areaAuditVenueHref(row.original.venue_id)
                : null
            }
            label={row.original.venue_name}
            id={row.original.venue_id}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "evidence",
        header: "Evidence",
        size: 360,
        cell: ({ row }) => {
          const summary = areaAuditEvidenceSummary(row.original);
          return summary ? (
            <span className="line-clamp-2 text-xs text-muted-foreground">
              {summary}
            </span>
          ) : (
            dash
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "first_seen_at",
        accessorKey: "first_seen_at",
        header: "First seen",
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs tabular-nums">
            {fmt(row.original.first_seen_at, "DD/MM HH:mm")}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "last_seen_at",
        accessorKey: "last_seen_at",
        header: "Last seen",
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs tabular-nums">
            {fmt(row.original.last_seen_at, "DD/MM HH:mm")}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "actions",
        header: "",
        size: 100,
        // Dismissal is the only action, and only an open finding can take it: a dismissed
        // or fixed row says so in the Status column instead.
        cell: ({ row }) => {
          const finding = row.original;
          if (areaAuditRowStatus(finding) !== "open") return null;
          return (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              disabled={dismissingId === finding.id}
              onClick={(event) => {
                event.stopPropagation();
                onDismiss(finding);
              }}
            >
              Dismiss
            </Button>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [onDismiss, dismissingId],
  );
